import { openDB, type DBSchema, type IDBPDatabase, type IDBPTransaction } from 'idb';
import {
  MAX_CONTAINERS,
  MAX_EVENTS,
  MAX_INCIDENT_RESOLUTIONS,
  MAX_TEMPERATURE_INCIDENTS,
  MAX_TEMPERATURE_READINGS,
  SCHEMA_VERSION,
  type AppMeta,
  type AuditRecord,
  type ContainerStatus,
  type CreateContainerInput,
  type DeclareTemperatureIncidentInput,
  type FoodContainer,
  type Freezer,
  type Household,
  type HouseholdDeletionReceipt,
  type HouseholdDeletionResult,
  type HouseholdPreferences,
  type IncidentResolution,
  type InventoryEvent,
  type InventoryBatchMutation,
  type InventoryMutation,
  type InventorySnapshot,
  type IsoDate,
  type RecordTemperatureReadingInput,
  type ResolveIncidentExposureInput,
  type SetupInput,
  type StorageNode,
  type TemperatureIncident,
  type TemperatureReading,
} from '../domain/model';
import type { MutationContext } from '../domain/inventory';
import { DomainError } from '../domain/errors';
import {
  assertCanAppendInventoryEvent,
  assertEventHistoryWithinLimit,
} from '../domain/event-limit';
import type { PersistencePort } from '../application/contracts';
import {
  allocateSmallestVisibleNumber,
  calculateVisibleNumberReleaseAt,
  hasVisibleNumber,
  holdsVisibleNumber,
  selectRestorationAssignment,
  VISIBLE_NUMBER_QUARANTINE,
  type VisibleNumberAssignment,
} from '../domain/visible-number';
import {
  assertSameStorageFamily,
  assertTrackingMatchesEquipment,
  storagePolicyFor,
} from '../domain/storage-policy';
import {
  cycleMatchesAuditHistory,
  validateInventorySnapshotCandidate,
} from './backup';
import { dueRotationAlerts } from '../domain/rotation-alerts';
import { assertRecipeShoppingLists } from '../domain/recipe-journey';
import {
  applyTemperatureIncidentFlags,
  createManualTemperatureReading,
  createTemperatureIncident,
  deriveIncidentStatus,
  findExposedContainerIds,
} from '../domain/temperature';

interface InventoryDb extends DBSchema {
  meta: {
    key: string;
    value: AppMeta;
  };
  households: {
    key: string;
    value: Household;
  };
  freezers: {
    key: string;
    value: Freezer;
    indexes: {
      'by-household': string;
    };
  };
  locations: {
    key: string;
    value: StorageNode;
    indexes: {
      'by-freezer': string;
      'by-parent': string;
    };
  };
  containers: {
    key: string;
    value: FoodContainer;
    indexes: {
      'by-household': string;
      'by-household-serial': [string, number];
      'by-location': string;
      'by-status': ContainerStatus;
    };
  };
  events: {
    key: string;
    value: InventoryEvent;
    indexes: {
      'by-container': string;
      'by-idempotency': string;
      'by-occurred-at': string;
    };
  };
  audits: {
    key: string;
    value: AuditRecord;
    indexes: {
      'by-location': string;
      'by-completed-at': string;
    };
  };
  preferences: {
    key: string;
    value: HouseholdPreferences;
  };
  temperatureReadings: {
    key: string;
    value: TemperatureReading;
    indexes: {
      'by-freezer': string;
      'by-recorded-at': string;
      'by-idempotency': string;
    };
  };
  temperatureIncidents: {
    key: string;
    value: TemperatureIncident;
    indexes: {
      'by-freezer': string;
      'by-status': TemperatureIncident['status'];
      'by-created-at': string;
      'by-idempotency': string;
    };
  };
  incidentResolutions: {
    key: string;
    value: IncidentResolution;
    indexes: {
      'by-incident': string;
      'by-container': string;
      'by-idempotency': string;
    };
  };
  householdDeletions: {
    key: string;
    value: HouseholdDeletionReceipt;
  };
}

const createV1Stores = (database: IDBPDatabase<InventoryDb>): void => {
  database.createObjectStore('meta', { keyPath: 'key' });
  database.createObjectStore('households', { keyPath: 'id' });

  const freezers = database.createObjectStore('freezers', { keyPath: 'id' });
  freezers.createIndex('by-household', 'householdId');

  const locations = database.createObjectStore('locations', { keyPath: 'id' });
  locations.createIndex('by-freezer', 'freezerId');
  locations.createIndex('by-parent', 'parentId');

  const containers = database.createObjectStore('containers', { keyPath: 'id' });
  containers.createIndex('by-household', 'householdId');
  containers.createIndex('by-household-serial', ['householdId', 'serialNumber'], { unique: true });
  containers.createIndex('by-location', 'locationId');
  containers.createIndex('by-status', 'status');

  const events = database.createObjectStore('events', { keyPath: 'id' });
  events.createIndex('by-container', 'containerId');
  events.createIndex('by-idempotency', 'idempotencyKey', { unique: true });
  events.createIndex('by-occurred-at', 'occurredAt');

  database.createObjectStore('preferences', { keyPath: 'householdId' });
};

export const openInventoryDatabase = async (
  name = 'inventaire-congelateurs',
): Promise<IDBPDatabase<InventoryDb>> =>
  openDB<InventoryDb>(name, SCHEMA_VERSION, {
    upgrade(database, oldVersion, _newVersion, transaction) {
      if (oldVersion < 1) {
        createV1Stores(database);
      }
      if (oldVersion < 2) {
        const audits = database.createObjectStore('audits', { keyPath: 'id' });
        audits.createIndex('by-location', 'locationId');
        audits.createIndex('by-completed-at', 'completedAt');
      }
      if (oldVersion < 3) {
        const containers = transaction.objectStore('containers');
        containers.deleteIndex('by-household-serial');
        containers.createIndex('by-household-serial', ['householdId', 'serialNumber']);
      }
      if (oldVersion < 4) {
        const readings = database.createObjectStore('temperatureReadings', { keyPath: 'id' });
        readings.createIndex('by-freezer', 'freezerId');
        readings.createIndex('by-recorded-at', 'recordedAt');
        readings.createIndex('by-idempotency', 'idempotencyKey', { unique: true });

        const incidents = database.createObjectStore('temperatureIncidents', { keyPath: 'id' });
        incidents.createIndex('by-freezer', 'freezerId');
        incidents.createIndex('by-status', 'status');
        incidents.createIndex('by-created-at', 'createdAt');
        incidents.createIndex('by-idempotency', 'idempotencyKey', { unique: true });

        const resolutions = database.createObjectStore('incidentResolutions', { keyPath: 'id' });
        resolutions.createIndex('by-incident', 'incidentId');
        resolutions.createIndex('by-container', 'containerId');
        resolutions.createIndex('by-idempotency', 'idempotencyKey', { unique: true });
      }
      if (oldVersion < 5) {
        database.createObjectStore('householdDeletions', { keyPath: 'householdId' });
      }
      // Le schéma 6 ajoute des champs métier migrés à l’initialisation, sans nouveau store.
    },
  });

const removeV3NumberFields = (container: FoodContainer): FoodContainer => {
  const legacy = structuredClone(container) as unknown as Record<string, unknown>;
  legacy.frozenOn = legacy.storageStartedOn;
  Reflect.deleteProperty(legacy, 'storageStartedOn');
  Reflect.deleteProperty(legacy, 'trackingMode');
  Reflect.deleteProperty(legacy, 'numberAssignedAt');
  Reflect.deleteProperty(legacy, 'numberReleasedAt');
  Reflect.deleteProperty(legacy, 'numberAssignmentKind');
  Reflect.deleteProperty(legacy, 'numberPolicyVersion');
  Reflect.deleteProperty(legacy, 'rotationAlerts');
  return legacy as unknown as FoodContainer;
};

const removeV3AuditFields = (audit: AuditRecord): AuditRecord => {
  const legacy = structuredClone(audit) as unknown as Record<string, unknown>;
  Reflect.deleteProperty(legacy, 'expectedCycleIds');
  Reflect.deleteProperty(legacy, 'seenCycleIds');
  Reflect.deleteProperty(legacy, 'missingCycleIds');
  return legacy as unknown as AuditRecord;
};

export const createLegacyV1Database = async (
  name: string,
  seed: InventorySnapshot | null = null,
): Promise<void> => {
  if (seed != null) {
    assertEventHistoryWithinLimit(seed.events.length);
  }
  const database = await openDB<InventoryDb>(name, 1, {
    upgrade(db) {
      createV1Stores(db);
    },
  });
  if (seed != null) {
    const transaction = database.transaction(
      ['meta', 'households', 'freezers', 'locations', 'containers', 'events', 'preferences'],
      'readwrite',
    );
    await transaction.objectStore('meta').put({
      ...seed.meta,
      schemaVersion: 1,
    });
    if (seed.household != null) {
      await transaction.objectStore('households').put(seed.household);
    }
    for (const freezer of seed.freezers) {
      await transaction.objectStore('freezers').put(freezer);
    }
    for (const location of seed.locations) {
      await transaction.objectStore('locations').put(location);
    }
    for (const container of seed.containers) {
      await transaction.objectStore('containers').put(removeV3NumberFields(container));
    }
    for (const event of seed.events) {
      await transaction.objectStore('events').put({
        ...event,
        payload: {
          ...event.payload,
          before:
            event.payload.before == null
              ? null
              : removeV3NumberFields(event.payload.before),
          after:
            event.payload.after == null
              ? null
              : removeV3NumberFields(event.payload.after),
        },
      });
    }
    if (seed.preferences != null) {
      await transaction.objectStore('preferences').put(seed.preferences);
    }
    await transaction.done;
  }
  database.close();
};

export const createLegacyV2Database = async (
  name: string,
  seed: InventorySnapshot,
): Promise<void> => {
  await createLegacyV1Database(name, seed);
  const database = await openDB<InventoryDb>(name, 2, {
    upgrade(db, oldVersion) {
      if (oldVersion < 2) {
        const audits = db.createObjectStore('audits', { keyPath: 'id' });
        audits.createIndex('by-location', 'locationId');
        audits.createIndex('by-completed-at', 'completedAt');
      }
    },
  });
  const transaction = database.transaction(['meta', 'audits'], 'readwrite');
  await transaction.objectStore('meta').put({
    ...seed.meta,
    schemaVersion: 2,
  });
  for (const audit of seed.audits) {
    await transaction.objectStore('audits').put(removeV3AuditFields(audit));
  }
  await transaction.done;
  database.close();
};

const nowIso = (): string => new Date().toISOString();
const randomId = (): string => crypto.randomUUID();
const RELEASED_STATUSES = new Set<FoodContainer['status']>(['out', 'discarded', 'archived']);

type LegacyContainer = Omit<
  FoodContainer,
  | 'trackingMode'
  | 'serialNumber'
  | 'numberAssignedAt'
  | 'numberAssignmentKind'
  | 'numberPolicyVersion'
  | 'rotationAlerts'
  | 'storageStartedOn'
> & {
  trackingMode?: FoodContainer['trackingMode'];
  serialNumber: number | null;
  numberAssignedAt?: FoodContainer['numberAssignedAt'];
  numberAssignmentKind?: FoodContainer['numberAssignmentKind'];
  numberPolicyVersion?: FoodContainer['numberPolicyVersion'];
  rotationAlerts?: FoodContainer['rotationAlerts'];
  storageStartedOn?: string;
  frozenOn?: string;
};

type LegacyAuditRecord = Omit<
  AuditRecord,
  'expectedCycleIds' | 'seenCycleIds' | 'missingCycleIds'
> &
  Partial<
    Pick<AuditRecord, 'expectedCycleIds' | 'seenCycleIds' | 'missingCycleIds'>
  >;

const needsContainerPolicyBackfill = (
  container: Partial<FoodContainer>,
  fridgeLocationIds: ReadonlySet<string>,
): boolean => {
  const belongsToFridge =
    typeof container.locationId === 'string' &&
    fridgeLocationIds.has(container.locationId);
  if (belongsToFridge) {
    return (
      container.trackingMode !== 'internal-only' ||
      container.serialNumber !== null ||
      container.numberAssignedAt !== null ||
      container.numberReleasedAt !== null ||
      container.numberAssignmentKind !== null ||
      container.numberPolicyVersion !== null ||
      container.horizonRule != null ||
      container.qualityHorizonOn != null ||
      !Array.isArray(container.rotationAlerts) ||
      container.rotationAlerts.length !== 0 ||
      typeof container.storageStartedOn !== 'string'
    );
  }
  return (
    !['visible-number', 'internal-only'].includes(String(container.trackingMode)) ||
    typeof container.storageStartedOn !== 'string' ||
    (container.trackingMode === 'visible-number'
      ? typeof container.numberPolicyVersion !== 'string'
      : container.numberPolicyVersion !== null) ||
    (container.trackingMode === 'internal-only' &&
      (container.horizonRule != null || container.qualityHorizonOn != null)) ||
    !Array.isArray(container.rotationAlerts)
  );
};

const migrateContainerToCurrent = (
  container: LegacyContainer,
  fridgeLocationIds: ReadonlySet<string>,
): FoodContainer => {
  const storageStartedOn =
    container.storageStartedOn ?? container.frozenOn ?? container.createdAt.slice(0, 10);
  const base = {
    ...container,
    storageStartedOn,
  };
  Reflect.deleteProperty(base, 'frozenOn');
  if (fridgeLocationIds.has(container.locationId)) {
    const removedLegacyHorizon =
      container.horizonRule != null || container.qualityHorizonOn != null;
    return {
      ...base,
      trackingMode: 'internal-only',
      serialNumber: null,
      numberAssignedAt: null,
      numberReleasedAt: null,
      numberAssignmentKind: null,
      numberPolicyVersion: null,
      qualityHorizonOn: null,
      horizonRule: null,
      rotationAlerts: [],
      notes:
        removedLegacyHorizon &&
        !container.notes.includes('Repère de congélation historique retiré')
          ? [container.notes, 'Repère de congélation historique retiré lors de la migration frigo.']
              .filter(Boolean)
              .join('\n')
          : container.notes,
    };
  }
  if (container.serialNumber == null) {
    throw new DomainError(
      'Un ancien contenant de congélateur ne possède aucun numéro visible.',
      'INVALID_STATE',
    );
  }
  return {
    ...base,
    trackingMode: 'visible-number',
    serialNumber: container.serialNumber,
    numberAssignedAt: container.numberAssignedAt ?? container.createdAt,
    numberReleasedAt: RELEASED_STATUSES.has(container.status)
      ? container.numberPolicyVersion === VISIBLE_NUMBER_QUARANTINE.version &&
        container.numberReleasedAt != null
        ? container.numberReleasedAt
        : calculateVisibleNumberReleaseAt(container.updatedAt)
      : null,
    numberAssignmentKind: container.numberAssignmentKind ?? 'legacy',
    numberPolicyVersion: VISIBLE_NUMBER_QUARANTINE.version,
    rotationAlerts: container.rotationAlerts ?? [],
  };
};

interface StoredMigrationCandidate {
  meta: AppMeta;
  households: Household[];
  freezers: Freezer[];
  locations: StorageNode[];
  containers: FoodContainer[];
  events: InventoryEvent[];
  audits: AuditRecord[];
  preferences: HouseholdPreferences[];
  temperatureReadings: TemperatureReading[];
  temperatureIncidents: TemperatureIncident[];
  incidentResolutions: IncidentResolution[];
}

const normalizeHouseholdPreferences = (
  preferences: HouseholdPreferences,
): HouseholdPreferences => ({
  ...preferences,
  reminderFrequency: preferences.reminderFrequency ?? 'off',
  reminderRecipient: preferences.reminderRecipient ?? 'in-app',
  lastReminderOn: preferences.lastReminderOn ?? null,
  recipeShoppingLists: Array.isArray(preferences.recipeShoppingLists)
    ? structuredClone(preferences.recipeShoppingLists)
    : [],
});

export interface IndexedDbMigrationHooks {
  beforeCommit?: () => void;
}

const validateStoredMigrationCandidate = (
  candidate: StoredMigrationCandidate,
): void => {
  const householdIds = new Set(candidate.households.map((household) => household.id));
  const requireKnownHousehold = (householdId: string, label: string): void => {
    if (!householdIds.has(householdId)) {
      throw new Error(`${label} appartient à un foyer absent.`);
    }
  };
  if (
    candidate.meta.householdId != null &&
    !householdIds.has(candidate.meta.householdId)
  ) {
    throw new Error('Le foyer actif de la migration est absent.');
  }
  for (const freezer of candidate.freezers) {
    requireKnownHousehold(freezer.householdId, 'Un appareil');
  }
  for (const container of candidate.containers) {
    requireKnownHousehold(container.householdId, 'Un contenant');
  }
  for (const event of candidate.events) {
    requireKnownHousehold(event.householdId, 'Un événement');
  }
  for (const audit of candidate.audits) {
    requireKnownHousehold(audit.householdId, 'Un audit');
  }
  for (const preference of candidate.preferences) {
    requireKnownHousehold(preference.householdId, 'Une préférence');
  }
  for (const reading of candidate.temperatureReadings) {
    requireKnownHousehold(reading.householdId, 'Un relevé');
  }
  for (const incident of candidate.temperatureIncidents) {
    requireKnownHousehold(incident.householdId, 'Un incident');
  }
  for (const resolution of candidate.incidentResolutions) {
    requireKnownHousehold(resolution.householdId, 'Une décision d’incident');
  }

  const freezersById = new Map(candidate.freezers.map((freezer) => [freezer.id, freezer]));
  for (const location of candidate.locations) {
    const freezer = freezersById.get(location.freezerId);
    if (freezer == null) {
      throw new Error('Un emplacement de migration référence un appareil absent.');
    }
  }

  if (candidate.households.length === 0) {
    validateInventorySnapshotCandidate(
      {
        meta: candidate.meta,
        household: null,
        freezers: [],
        locations: [],
        containers: [],
        events: [],
        audits: [],
        temperatureReadings: [],
        temperatureIncidents: [],
        incidentResolutions: [],
        preferences: null,
      },
      { allowEmptyHousehold: true },
    );
    return;
  }

  for (const household of candidate.households) {
    const freezers = candidate.freezers.filter(
      (freezer) => freezer.householdId === household.id,
    );
    const freezerIds = new Set(freezers.map((freezer) => freezer.id));
    const locations = candidate.locations.filter((location) =>
      freezerIds.has(location.freezerId),
    );
    validateInventorySnapshotCandidate({
      meta: {
        ...candidate.meta,
        householdId: household.id,
      },
      household,
      freezers,
      locations,
      containers: candidate.containers.filter(
        (container) => container.householdId === household.id,
      ),
      events: candidate.events.filter((event) => event.householdId === household.id),
      audits: candidate.audits.filter((audit) => audit.householdId === household.id),
      temperatureReadings: candidate.temperatureReadings.filter(
        (reading) => reading.householdId === household.id,
      ),
      temperatureIncidents: candidate.temperatureIncidents.filter(
        (incident) => incident.householdId === household.id,
      ),
      incidentResolutions: candidate.incidentResolutions.filter(
        (resolution) => resolution.householdId === household.id,
      ),
      preferences:
        candidate.preferences.find(
          (preference) => preference.householdId === household.id,
        ) ?? null,
    });
  }
};

const migrateStoredDataToCurrentSchema = async (
  database: IDBPDatabase<InventoryDb>,
  hooks: IndexedDbMigrationHooks,
): Promise<void> => {
  const transaction = database.transaction(
    [
      'meta',
      'households',
      'containers',
      'events',
      'audits',
      'preferences',
      'freezers',
      'locations',
      'temperatureReadings',
      'temperatureIncidents',
      'incidentResolutions',
    ],
    'readwrite',
  );
  const transactionCompletion = transaction.done;
  void transactionCompletion.catch(() => undefined);
  try {
    const [
      meta,
      events,
      containers,
      freezers,
      locations,
      households,
      audits,
      preferences,
      temperatureReadings,
      temperatureIncidents,
      incidentResolutions,
    ] = await Promise.all([
      transaction.objectStore('meta').get('app'),
      transaction.objectStore('events').getAll(undefined, MAX_EVENTS + 1),
      transaction.objectStore('containers').getAll(),
      transaction.objectStore('freezers').getAll(),
      transaction.objectStore('locations').getAll(),
      transaction.objectStore('households').getAll(),
      transaction.objectStore('audits').getAll(),
      transaction.objectStore('preferences').getAll(),
      transaction.objectStore('temperatureReadings').getAll(),
      transaction.objectStore('temperatureIncidents').getAll(),
      transaction.objectStore('incidentResolutions').getAll(),
    ]);
    assertEventHistoryWithinLimit(events.length);
    if (meta == null) {
      const hasOrphanedData = [
        events,
        containers,
        freezers,
        locations,
        households,
        audits,
        preferences,
        temperatureReadings,
        temperatureIncidents,
        incidentResolutions,
      ].some((records) => records.length > 0);
      if (hasOrphanedData) {
        throw new Error('Une base sans métadonnées contient encore des données métier.');
      }
      const now = nowIso();
      await transaction.objectStore('meta').add({
        key: 'app',
        schemaVersion: SCHEMA_VERSION,
        householdId: null,
        deviceId: randomId(),
        deviceSequence: 0,
        nextSerial: 1,
        persistentStorage: 'unknown',
        demoSeededAt: null,
        createdAt: now,
        updatedAt: now,
      });
      await transactionCompletion;
      return;
    }
    const fridgeIds = new Set(
      freezers
        .filter((freezer) => freezer.equipmentKind === 'fridge')
        .map((freezer) => freezer.id),
    );
    const fridgeLocationIds = new Set(
      locations
        .filter((location) => fridgeIds.has(location.freezerId))
        .map((location) => location.id),
    );
    const needsPolicyBackfill =
      containers.some((container) =>
        needsContainerPolicyBackfill(container, fridgeLocationIds),
      ) ||
      events.some((event) =>
        [event.payload.before, event.payload.after].some(
          (state) =>
            state != null &&
            needsContainerPolicyBackfill(state, fridgeLocationIds),
        ),
      ) ||
      audits.some((audit) => fridgeIds.has(audit.freezerId));
    if (meta.schemaVersion > SCHEMA_VERSION) {
      throw new Error(
        `Le schéma local ${meta.schemaVersion} est plus récent que la version prise en charge.`,
      );
    }
    if (meta.schemaVersion === SCHEMA_VERSION && !needsPolicyBackfill) {
      await transactionCompletion;
      return;
    }
    const migratedContainers = (
      containers as unknown as LegacyContainer[]
    ).map((container) => migrateContainerToCurrent(container, fridgeLocationIds));
    const migratedEvents = events.map((event) => ({
      ...event,
      payload: {
        ...event.payload,
        before:
          event.payload.before == null
            ? null
            : migrateContainerToCurrent(event.payload.before, fridgeLocationIds),
        after:
          event.payload.after == null
            ? null
            : migrateContainerToCurrent(event.payload.after, fridgeLocationIds),
      },
    }));
    const legacyAudits = audits as LegacyAuditRecord[];
    const containersById = new Map(
      migratedContainers.map((container) => [container.id, container]),
    );
    const locationsById = new Map(locations.map((location) => [location.id, location]));
    const freezersById = new Map(freezers.map((freezer) => [freezer.id, freezer]));
    const resolveAuditPairs = (
      audit: LegacyAuditRecord,
      cycleIds: string[] | undefined,
      serials: number[],
      listLabel: string,
    ): { cycleIds: string[]; serials: number[] } => {
      if (
        !Array.isArray(serials) ||
        serials.some(
          (serialNumber) => !Number.isInteger(serialNumber) || serialNumber <= 0,
        )
      ) {
        throw new DomainError(
          `L’audit historique ${audit.id} contient une liste ${listLabel} invalide.`,
          'INVALID_STATE',
        );
      }
      const hasExplicitCycleIds = Array.isArray(cycleIds);
      if (meta.schemaVersion >= 3 && !hasExplicitCycleIds) {
        throw new DomainError(
          `L’audit historique ${audit.id} ne contient pas ses identifiants de cycle explicites.`,
          'INVALID_STATE',
        );
      }
      if (hasExplicitCycleIds) {
        if (cycleIds.length !== serials.length) {
          throw new DomainError(
            `L’audit historique ${audit.id} associe incomplètement ses cycles et numéros ${listLabel}.`,
            'INVALID_STATE',
          );
        }
        for (const [index, cycleId] of cycleIds.entries()) {
          const serialNumber = serials[index]!;
          const container = containersById.get(cycleId);
          if (container == null) {
            throw new DomainError(
              `Le cycle ${cycleId} est introuvable dans l’audit historique ${audit.id}.`,
              'INVALID_STATE',
            );
          }
          if (
            !hasVisibleNumber(container) ||
            container.serialNumber !== serialNumber ||
            !cycleMatchesAuditHistory(
              container,
              audit as AuditRecord,
              serialNumber,
              migratedEvents,
              locations,
            )
          ) {
            throw new DomainError(
              `Le cycle ${cycleId} ne correspond pas au numéro, à l’appareil et à l’emplacement de l’audit historique ${audit.id}.`,
              'INVALID_STATE',
            );
          }
        }
        return { cycleIds: [...cycleIds], serials: [...serials] };
      }
      const resolvedCycleIds = serials.map((serialNumber) => {
        const matches = migratedContainers.filter((container) => {
          return cycleMatchesAuditHistory(
            container,
            audit as AuditRecord,
            serialNumber,
            migratedEvents,
            locations,
          );
        });
        if (matches.length === 0) {
          throw new DomainError(
            `Le cycle du numéro ${serialNumber} est introuvable dans l’audit historique ${audit.id}.`,
            'INVALID_STATE',
          );
        }
        if (matches.length > 1) {
          throw new DomainError(
            `L’identité du cycle du numéro ${serialNumber} est ambiguë dans l’audit historique ${audit.id}.`,
            'INVALID_STATE',
          );
        }
        return matches[0]!.id;
      });
      return { cycleIds: resolvedCycleIds, serials: [...serials] };
    };
    const migratedAudits = legacyAudits.flatMap((audit): AuditRecord[] => {
      const freezer = freezersById.get(audit.freezerId);
      const location = locationsById.get(audit.locationId);
      if (freezer == null || location == null || location.freezerId !== freezer.id) {
        throw new DomainError(
          `L’appareil ou l’emplacement de l’audit historique ${audit.id} est introuvable.`,
          'INVALID_STATE',
        );
      }
      if (fridgeIds.has(audit.freezerId)) {
        return [];
      }
      const expected = resolveAuditPairs(
        audit,
        audit.expectedCycleIds,
        audit.expectedSerials,
        'attendue',
      );
      const seen = resolveAuditPairs(
        audit,
        audit.seenCycleIds,
        audit.seenSerials,
        'vue',
      );
      const missing = resolveAuditPairs(
        audit,
        audit.missingCycleIds,
        audit.missingSerials,
        'manquante',
      );
      return [{
        ...audit,
        expectedCycleIds: expected.cycleIds,
        expectedSerials: expected.serials,
        seenCycleIds: seen.cycleIds,
        seenSerials: seen.serials,
        missingCycleIds: missing.cycleIds,
        missingSerials: missing.serials,
      }];
    });
    const migratedPreferences = preferences.map((preference) => ({
      ...normalizeHouseholdPreferences(preference),
      alertHorizonDays: 7,
    }));
    const migratedTemperatureIncidents = [...temperatureIncidents];
    if (temperatureIncidents.length === 0) {
      const freezerByLocation = new Map(
        locations.map((location) => [location.id, location.freezerId]),
      );
      const unresolvedByFreezer = new Map<string, FoodContainer[]>();
      for (const container of migratedContainers) {
        if (container.incidentStatus !== 'unresolved') continue;
        const freezerId = freezerByLocation.get(container.locationId);
        if (freezerId == null) continue;
        const list = unresolvedByFreezer.get(freezerId) ?? [];
        list.push(container);
        unresolvedByFreezer.set(freezerId, list);
      }
      for (const [freezerId, affected] of unresolvedByFreezer) {
        const createdAt =
          affected.map((container) => container.updatedAt).sort()[0] ?? nowIso();
        migratedTemperatureIncidents.push({
          id: randomId(),
          idempotencyKey: `migration-v4:${freezerId}`,
          householdId: meta.householdId ?? affected[0]!.householdId,
          freezerId,
          kind: 'unknown',
          source: 'manual_observation',
          started: { value: null, certainty: 'unknown' },
          ended: { value: null, certainty: 'unknown' },
          maximumTemperatureC: null,
          note:
            affected
              .map((container) => container.incidentNote.trim())
              .filter(Boolean)
              .join(' · ') ||
            'Incident antérieur migré depuis une fiche de contenant.',
          affectedContainerIds: affected.map((container) => container.id).sort(),
          status: 'open',
          createdAt,
          updatedAt: createdAt,
        });
      }
    }
    const migratedMeta: AppMeta = {
      ...meta,
      schemaVersion: SCHEMA_VERSION,
      updatedAt: nowIso(),
    };
    validateStoredMigrationCandidate({
      meta: migratedMeta,
      households,
      freezers,
      locations,
      containers: migratedContainers,
      events: migratedEvents,
      audits: migratedAudits,
      preferences: migratedPreferences,
      temperatureReadings,
      temperatureIncidents: migratedTemperatureIncidents,
      incidentResolutions,
    });

    for (const container of migratedContainers) {
      await transaction.objectStore('containers').put(container);
    }
    for (const event of migratedEvents) {
      await transaction.objectStore('events').put(event);
    }
    const migratedAuditIds = new Set(migratedAudits.map((audit) => audit.id));
    for (const audit of legacyAudits) {
      if (!migratedAuditIds.has(audit.id)) {
        await transaction.objectStore('audits').delete(audit.id);
      }
    }
    for (const audit of migratedAudits) {
      await transaction.objectStore('audits').put(audit);
    }
    for (const preference of migratedPreferences) {
      await transaction.objectStore('preferences').put(preference);
    }
    for (const incident of migratedTemperatureIncidents) {
      await transaction.objectStore('temperatureIncidents').put(incident);
    }
    hooks.beforeCommit?.();
    await transaction.objectStore('meta').put(migratedMeta);
    await transactionCompletion;
  } catch (error) {
    try {
      transaction.abort();
    } catch {
      // La transaction peut déjà être terminée après une erreur native IndexedDB.
    }
    try {
      await transactionCompletion;
    } catch {
      // L’erreur métier d’origine reste celle transmise à l’appelant.
    }
    throw error;
  }
};

const snapshotStores = ['meta', 'households', 'freezers', 'locations', 'containers', 'events', 'audits', 'preferences', 'temperatureReadings', 'temperatureIncidents', 'incidentResolutions'] as const;
type SnapshotTransaction = IDBPTransaction<InventoryDb, typeof snapshotStores, 'readonly' | 'readwrite'>;

export class IndexedDbInventoryRepository implements PersistencePort {
  private databasePromise: Promise<IDBPDatabase<InventoryDb>> | null = null;
  private initializationPromise: Promise<void> | null = null;

  constructor(
    private readonly databaseName = 'inventaire-congelateurs',
    private readonly migrationHooks: IndexedDbMigrationHooks = {},
  ) {}

  private database(): Promise<IDBPDatabase<InventoryDb>> {
    this.databasePromise ??= openInventoryDatabase(this.databaseName);
    return this.databasePromise;
  }

  async initialize(): Promise<void> {
    this.initializationPromise ??= (async () => {
      const database = await this.database();
      await migrateStoredDataToCurrentSchema(database, this.migrationHooks);
    })();
    try {
      await this.initializationPromise;
    } catch (error) {
      // Une initialisation rejetée doit pouvoir être rejouée après correction
      // de la source locale ou réouverture, sans conserver une promesse rouge.
      this.initializationPromise = null;
      throw error;
    }
  }

  async loadSnapshot(): Promise<InventorySnapshot> {
    await this.initialize();
    const database = await this.database();
    const transaction = database.transaction(snapshotStores, 'readonly');
    const snapshot = await this.readSnapshot(transaction);
    await transaction.done;
    return snapshot;
  }

  private async readSnapshot(transaction: SnapshotTransaction): Promise<InventorySnapshot> {
    const meta = await transaction.objectStore('meta').get('app');
    if (meta == null) {
      throw new Error('Métadonnées locales introuvables.');
    }
    const household =
      meta.householdId == null ? null : ((await transaction.objectStore('households').get(meta.householdId)) ?? null);
    if (household == null) {
      return {
        meta,
        household: null,
        freezers: [],
        locations: [],
        containers: [],
        events: [],
        audits: [],
        temperatureReadings: [],
        temperatureIncidents: [],
        incidentResolutions: [],
        preferences: null,
      };
    }

    const freezers = await transaction.objectStore('freezers').index('by-household').getAll(household.id);
    const freezerIds = new Set(freezers.map((freezer) => freezer.id));
    const allLocations = await transaction.objectStore('locations').getAll();
    const locations = allLocations.filter((location) => freezerIds.has(location.freezerId));
    const containers = await transaction.objectStore('containers').index('by-household').getAll(household.id, MAX_CONTAINERS);
    const allEvents = await transaction.objectStore('events').index('by-occurred-at').getAll(undefined,
      MAX_EVENTS + 1,
    );
    assertEventHistoryWithinLimit(allEvents.length);
    const events = allEvents.filter((event) => event.householdId === household.id);
    const audits = (await transaction.objectStore('audits').getAll()).filter((audit) => audit.householdId === household.id);
    const storedPreferences = (await transaction.objectStore('preferences').get(household.id)) ?? null;
    const preferences = storedPreferences == null
      ? null
      : normalizeHouseholdPreferences(storedPreferences);
    const temperatureReadings = (
      await transaction.objectStore('temperatureReadings').index('by-recorded-at').getAll(undefined,
        MAX_TEMPERATURE_READINGS,
      )
    ).filter((reading) => reading.householdId === household.id);
    const temperatureIncidents = (
      await transaction.objectStore('temperatureIncidents').index('by-created-at').getAll(undefined,
        MAX_TEMPERATURE_INCIDENTS,
      )
    )
      .filter((incident) => incident.householdId === household.id)
      .map((incident) => ({ ...incident }));
    const incidentResolutions = (await transaction.objectStore('incidentResolutions').getAll())
      .filter((resolution) => resolution.householdId === household.id)
      .slice(0, MAX_INCIDENT_RESOLUTIONS);
    for (const incident of temperatureIncidents) {
      incident.status = deriveIncidentStatus(incident, incidentResolutions);
    }

    return {
      meta,
      household,
      freezers,
      locations,
      containers: applyTemperatureIncidentFlags(
        containers,
        temperatureIncidents,
        incidentResolutions,
      ),
      events,
      audits,
      temperatureReadings,
      temperatureIncidents,
      incidentResolutions,
      preferences,
    };
  }

  async setup(input: SetupInput): Promise<InventorySnapshot> {
    if (input.equipmentKind === 'pantry') {
      throw new DomainError(
        'Commencez par un congélateur ou un réfrigérateur ; la réserve s’ajoute ensuite dans Plus.',
        'INVALID_STATE',
      );
    }
    await this.initialize();
    const database = await this.database();
    const transaction = database.transaction(
      ['meta', 'households', 'freezers', 'locations', 'preferences'],
      'readwrite',
    );
    const meta = await transaction.objectStore('meta').get('app');
    if (meta == null) {
      throw new Error('Métadonnées locales introuvables.');
    }
    if (meta.householdId != null) {
      await transaction.done;
      return this.loadSnapshot();
    }

    const now = nowIso();
    const household: Household = {
      id: randomId(),
      name: input.householdName.trim() || 'Mon foyer',
      mode: 'local',
      locale: 'fr-FR',
      timezone: input.timezone,
      createdAt: now,
    };
    const freezer: Freezer = {
      id: randomId(),
      householdId: household.id,
      name:
        input.freezerName.trim() ||
        (input.equipmentKind === 'fridge' ? 'Réfrigérateur principal' : 'Congélateur principal'),
      equipmentKind: input.equipmentKind,
      declaredTemperatureC: storagePolicyFor(input.equipmentKind).defaultDeclaredTemperatureC,
      archivedAt: null,
      createdAt: now,
    };
    const locationNames = input.locationNames.map((name) => name.trim()).filter(Boolean);
    const effectiveNames = locationNames.length > 0 ? locationNames : ['Emplacement à préciser'];
    const locations: StorageNode[] = effectiveNames.map((name, position) => ({
      id: randomId(),
      freezerId: freezer.id,
      parentId: null,
      name,
      position,
      archivedAt: null,
    }));
    const preferences: HouseholdPreferences = {
      householdId: household.id,
      exclusions: [],
      safetyAcknowledged: input.safetyAcknowledged,
      alertHorizonDays: 7,
      lastLocationId: locations[0]?.id ?? null,
      reminderFrequency: 'off',
      reminderRecipient: 'in-app',
      lastReminderOn: null,
      recipeShoppingLists: [],
      createdAt: now,
      updatedAt: now,
    };

    await transaction.objectStore('households').add(household);
    await transaction.objectStore('freezers').add(freezer);
    for (const location of locations) {
      await transaction.objectStore('locations').add(location);
    }
    await transaction.objectStore('preferences').add(preferences);
    await transaction.objectStore('meta').put({
      ...meta,
      householdId: household.id,
      updatedAt: now,
    });
    await transaction.done;
    return this.loadSnapshot();
  }

  async createDraft(
    input: CreateContainerInput,
    idempotencyKey: string,
    factory: (
      id: string,
      assignment: VisibleNumberAssignment | null,
      input: CreateContainerInput,
      context: MutationContext,
    ) => InventoryMutation,
  ): Promise<InventoryMutation> {
    const database = await this.database();
    const transaction = database.transaction(
      ['meta', 'containers', 'events', 'locations', 'freezers'],
      'readwrite',
    );
    const existing = await transaction.objectStore('events').index('by-idempotency').get(idempotencyKey);
    if (existing?.payload.after != null) {
      await transaction.done;
      return { container: existing.payload.after, event: existing };
    }
    assertCanAppendInventoryEvent(await transaction.objectStore('events').count());
    const meta = await transaction.objectStore('meta').get('app');
    if (meta?.householdId == null) {
      throw new DomainError('Créez d’abord le foyer local.', 'INVALID_STATE');
    }
    const householdContainers = await transaction
      .objectStore('containers')
      .index('by-household')
      .getAll(meta.householdId, MAX_CONTAINERS);
    if (householdContainers.length >= MAX_CONTAINERS) {
      throw new DomainError(`La limite locale de ${MAX_CONTAINERS} contenants est atteinte.`, 'LIMIT_REACHED');
    }
    const sequence = meta.deviceSequence + 1;
    const context: MutationContext = {
      householdId: meta.householdId,
      deviceId: meta.deviceId,
      deviceSequence: sequence,
      eventId: randomId(),
      idempotencyKey,
      now: nowIso(),
    };
    const location = await transaction.objectStore('locations').get(input.locationId);
    const appliance =
      location == null
        ? null
        : await transaction.objectStore('freezers').get(location.freezerId);
    if (location == null || appliance?.householdId !== meta.householdId) {
      throw new DomainError('L’emplacement de l’appareil est introuvable.', 'NOT_FOUND');
    }
    const policy = storagePolicyFor(appliance.equipmentKind);
    const assignment =
      policy.trackingMode === 'visible-number'
        ? (() => {
            const serialNumber = allocateSmallestVisibleNumber(householdContainers, context.now);
            return {
              serialNumber,
              assignedAt: context.now,
              kind: householdContainers.some(
                (container) => container.serialNumber === serialNumber,
              )
                ? ('reused' as const)
                : ('first' as const),
            };
          })()
        : null;
    const mutation = factory(randomId(), assignment, input, context);
    assertTrackingMatchesEquipment(mutation.container, appliance.equipmentKind);
    if (assignment != null) {
      if (
        mutation.container.serialNumber !== assignment.serialNumber ||
        mutation.container.numberReleasedAt != null ||
        !holdsVisibleNumber(mutation.container)
      ) {
        throw new DomainError('L’attribution du numéro visible est incohérente.', 'INVALID_STATE');
      }
    } else if (
      mutation.container.status !== 'present' ||
      mutation.container.numberAssignedAt != null ||
      mutation.container.numberReleasedAt != null ||
      mutation.container.numberAssignmentKind != null ||
      mutation.container.numberPolicyVersion != null
    ) {
      throw new DomainError('L’entrée sans numéro est incohérente.', 'INVALID_STATE');
    }
    await transaction.objectStore('containers').add(mutation.container);
    await transaction.objectStore('events').add(mutation.event);
    await transaction.objectStore('meta').put({
      ...meta,
      deviceSequence: sequence,
      nextSerial:
        assignment == null
          ? meta.nextSerial
          : Math.max(meta.nextSerial, assignment.serialNumber + 1),
      updatedAt: context.now,
    });
    await transaction.done;
    return mutation;
  }

  async mutateContainer(
    containerId: string,
    idempotencyKey: string,
    factory: (container: FoodContainer, context: MutationContext) => InventoryMutation,
    expectedVersion?: number,
  ): Promise<InventoryMutation> {
    const database = await this.database();
    const transaction = database.transaction(
      ['meta', 'containers', 'events', 'locations', 'freezers'],
      'readwrite',
    );
    // A separate tab may submit the same visible action/key from an older view.
    // Check its version before treating an existing event as a successful retry.
    if (expectedVersion != null) {
      const current = await transaction.objectStore('containers').get(containerId);
      if (current == null || current.version !== expectedVersion) {
        await transaction.done;
        throw new DomainError('Le contenant a changé depuis son affichage.', 'STALE_CONTAINER');
      }
    }
    const existing = await transaction.objectStore('events').index('by-idempotency').get(idempotencyKey);
    if (existing?.payload.after != null) {
      await transaction.done;
      return { container: existing.payload.after, event: existing };
    }
    assertCanAppendInventoryEvent(await transaction.objectStore('events').count());
    const [meta, container] = await Promise.all([
      transaction.objectStore('meta').get('app'),
      transaction.objectStore('containers').get(containerId),
    ]);
    if (meta?.householdId == null) {
      throw new DomainError('Foyer local introuvable.', 'NOT_FOUND');
    }
    if (container == null) {
      throw new DomainError('Contenant introuvable.', 'NOT_FOUND');
    }
    const sourceLocation = await transaction.objectStore('locations').get(container.locationId);
    const sourceAppliance =
      sourceLocation == null
        ? null
        : await transaction.objectStore('freezers').get(sourceLocation.freezerId);
    if (
      sourceLocation == null ||
      sourceAppliance == null ||
      sourceAppliance.householdId !== meta.householdId
    ) {
      throw new DomainError('L’emplacement actuel est introuvable.', 'NOT_FOUND');
    }
    const sequence = meta.deviceSequence + 1;
    const context: MutationContext = {
      householdId: meta.householdId,
      deviceId: meta.deviceId,
      deviceSequence: sequence,
      eventId: randomId(),
      idempotencyKey,
      now: nowIso(),
    };
    const mutation = factory(container, context);
    const destinationLocation = await transaction
      .objectStore('locations')
      .get(mutation.container.locationId);
    const destinationAppliance =
      destinationLocation == null
        ? null
        : await transaction.objectStore('freezers').get(destinationLocation.freezerId);
    if (
      destinationLocation == null ||
      destinationAppliance == null ||
      destinationAppliance.householdId !== meta.householdId
    ) {
      throw new DomainError('L’emplacement de destination est introuvable.', 'NOT_FOUND');
    }
    assertSameStorageFamily(sourceAppliance.equipmentKind, destinationAppliance.equipmentKind);
    assertTrackingMatchesEquipment(mutation.container, destinationAppliance.equipmentKind);
    if (holdsVisibleNumber(mutation.container)) {
      const sameNumber = await transaction
        .objectStore('containers')
        .index('by-household-serial')
        .getAll([meta.householdId, mutation.container.serialNumber]);
      if (
        sameNumber.some(
          (candidate) =>
            candidate.id !== mutation.container.id && holdsVisibleNumber(candidate),
        )
      ) {
        throw new DomainError(
          `Le numéro ${mutation.container.serialNumber} est déjà utilisé par un contenant présent.`,
          'INVALID_STATE',
        );
      }
      if (mutation.container.numberReleasedAt != null) {
        throw new DomainError('Un numéro actif ne peut pas être marqué comme libéré.', 'INVALID_STATE');
      }
    }
    await transaction.objectStore('containers').put(mutation.container);
    await transaction.objectStore('events').add(mutation.event);
    await transaction.objectStore('meta').put({
      ...meta,
      deviceSequence: sequence,
      updatedAt: context.now,
    });
    await transaction.done;
    return mutation;
  }

  async mutateContainers(
    containerIds: string[],
    idempotencyKey: string,
    factory: (
      containers: FoodContainer[],
      householdContainers: FoodContainer[],
      contexts: MutationContext[],
    ) => InventoryMutation[],
  ): Promise<InventoryBatchMutation> {
    const uniqueIds = [...new Set(containerIds)];
    if (uniqueIds.length === 0 || uniqueIds.length !== containerIds.length || uniqueIds.length > 50) {
      throw new DomainError(
        'La déduction groupée doit cibler entre 1 et 50 aliments distincts.',
        'INVALID_STATE',
      );
    }
    const database = await this.database();
    const transaction = database.transaction(
      ['meta', 'containers', 'events', 'locations', 'freezers'],
      'readwrite',
    );
    const eventsStore = transaction.objectStore('events');
    const existing = await Promise.all(
      uniqueIds.map((_, index) =>
        eventsStore.index('by-idempotency').get(`${idempotencyKey}:${index}`),
      ),
    );
    if (existing.every((event) => event?.payload.after != null)) {
      await transaction.done;
      return {
        idempotencyKey,
        mutations: existing.map((event) => ({
          container: event!.payload.after!,
          event: event!,
        })),
      };
    }
    if (existing.some((event) => event != null)) {
      throw new DomainError(
        'Une opération groupée incomplète a été détectée ; aucune nouvelle écriture n’a été faite.',
        'INVALID_STATE',
      );
    }
    const [meta, eventCount] = await Promise.all([
      transaction.objectStore('meta').get('app'),
      eventsStore.count(),
    ]);
    if (meta?.householdId == null) {
      throw new DomainError('Foyer local introuvable.', 'NOT_FOUND');
    }
    assertEventHistoryWithinLimit(eventCount + uniqueIds.length);
    const householdContainers = await transaction
      .objectStore('containers')
      .index('by-household')
      .getAll(meta.householdId, MAX_CONTAINERS);
    const byId = new Map(householdContainers.map((container) => [container.id, container]));
    const targets = uniqueIds.map((containerId) => {
      const container = byId.get(containerId);
      if (container == null) {
        throw new DomainError('Un aliment de la recette est introuvable.', 'NOT_FOUND');
      }
      return container;
    });
    const now = nowIso();
    const contexts = uniqueIds.map((_, index): MutationContext => ({
      householdId: meta.householdId!,
      deviceId: meta.deviceId,
      deviceSequence: meta.deviceSequence + index + 1,
      eventId: randomId(),
      idempotencyKey: `${idempotencyKey}:${index}`,
      now,
    }));
    const mutations = factory(
      structuredClone(targets),
      structuredClone(householdContainers),
      contexts,
    );
    if (
      mutations.length !== targets.length ||
      mutations.some(
        (mutation, index) =>
          mutation.container.id !== targets[index]!.id ||
          mutation.event.idempotencyKey !== contexts[index]!.idempotencyKey ||
          mutation.event.deviceSequence !== contexts[index]!.deviceSequence,
      )
    ) {
      throw new DomainError('La mutation groupée est incohérente.', 'INVALID_STATE');
    }

    const projected = new Map(householdContainers.map((container) => [container.id, container]));
    for (const mutation of mutations) {
      const sourceContainer = byId.get(mutation.container.id)!;
      const sourceLocation = await transaction
        .objectStore('locations')
        .get(sourceContainer.locationId);
      const sourceAppliance =
        sourceLocation == null
          ? null
          : await transaction.objectStore('freezers').get(sourceLocation.freezerId);
      const destinationLocation = await transaction
        .objectStore('locations')
        .get(mutation.container.locationId);
      const destinationAppliance =
        destinationLocation == null
          ? null
          : await transaction.objectStore('freezers').get(destinationLocation.freezerId);
      if (
        sourceLocation == null ||
        sourceAppliance == null ||
        sourceAppliance.householdId !== meta.householdId ||
        destinationLocation == null ||
        destinationAppliance == null ||
        destinationAppliance.householdId !== meta.householdId
      ) {
        throw new DomainError('L’emplacement de destination est introuvable.', 'NOT_FOUND');
      }
      assertSameStorageFamily(sourceAppliance.equipmentKind, destinationAppliance.equipmentKind);
      assertTrackingMatchesEquipment(mutation.container, destinationAppliance.equipmentKind);
      projected.set(mutation.container.id, mutation.container);
    }
    const heldNumbers = new Map<number, string>();
    for (const container of projected.values()) {
      if (!holdsVisibleNumber(container)) continue;
      const owner = heldNumbers.get(container.serialNumber);
      if (owner != null && owner !== container.id) {
        throw new DomainError(
          `Le numéro ${container.serialNumber} est déjà utilisé par un autre contenant.`,
          'INVALID_STATE',
        );
      }
      heldNumbers.set(container.serialNumber, container.id);
    }

    for (const mutation of mutations) {
      await transaction.objectStore('containers').put(mutation.container);
      await eventsStore.add(mutation.event);
    }
    await transaction.objectStore('meta').put({
      ...meta,
      deviceSequence: meta.deviceSequence + mutations.length,
      updatedAt: now,
    });
    await transaction.done;
    return { idempotencyKey, mutations };
  }

  async reactivateContainer(
    containerId: string,
    idempotencyKey: string,
    factory: (
      container: FoodContainer,
      assignment: VisibleNumberAssignment | null,
      context: MutationContext,
    ) => InventoryMutation,
    expectedVersion?: number,
  ): Promise<InventoryMutation> {
    const database = await this.database();
    const transaction = database.transaction(
      ['meta', 'containers', 'events', 'locations', 'freezers'],
      'readwrite',
    );
    if (expectedVersion != null) {
      const current = await transaction.objectStore('containers').get(containerId);
      if (current == null || current.version !== expectedVersion) {
        await transaction.done;
        throw new DomainError('Le contenant a changé depuis son affichage.', 'STALE_CONTAINER');
      }
    }
    const existing = await transaction.objectStore('events').index('by-idempotency').get(idempotencyKey);
    if (existing?.payload.after != null) {
      await transaction.done;
      return { container: existing.payload.after, event: existing };
    }
    assertCanAppendInventoryEvent(await transaction.objectStore('events').count());
    const [meta, container] = await Promise.all([
      transaction.objectStore('meta').get('app'),
      transaction.objectStore('containers').get(containerId),
    ]);
    if (meta?.householdId == null) {
      throw new DomainError('Foyer local introuvable.', 'NOT_FOUND');
    }
    if (container == null) {
      throw new DomainError('Contenant introuvable.', 'NOT_FOUND');
    }
    const sourceLocation = await transaction.objectStore('locations').get(container.locationId);
    const sourceAppliance =
      sourceLocation == null
        ? null
        : await transaction.objectStore('freezers').get(sourceLocation.freezerId);
    if (
      sourceLocation == null ||
      sourceAppliance == null ||
      sourceAppliance.householdId !== meta.householdId
    ) {
      throw new DomainError('L’emplacement actuel est introuvable.', 'NOT_FOUND');
    }
    const householdContainers = await transaction
      .objectStore('containers')
      .index('by-household')
      .getAll(meta.householdId, MAX_CONTAINERS);
    const sequence = meta.deviceSequence + 1;
    const context: MutationContext = {
      householdId: meta.householdId,
      deviceId: meta.deviceId,
      deviceSequence: sequence,
      eventId: randomId(),
      idempotencyKey,
      now: nowIso(),
    };
    const assignment = hasVisibleNumber(container)
      ? selectRestorationAssignment(container, householdContainers, context.now)
      : null;
    const mutation = factory(container, assignment, context);
    const destinationLocation = await transaction
      .objectStore('locations')
      .get(mutation.container.locationId);
    const destinationAppliance =
      destinationLocation == null
        ? null
        : await transaction.objectStore('freezers').get(destinationLocation.freezerId);
    if (
      destinationLocation == null ||
      destinationAppliance == null ||
      destinationAppliance.householdId !== meta.householdId
    ) {
      throw new DomainError('L’emplacement de restauration est introuvable.', 'NOT_FOUND');
    }
    assertSameStorageFamily(sourceAppliance.equipmentKind, destinationAppliance.equipmentKind);
    assertTrackingMatchesEquipment(mutation.container, destinationAppliance.equipmentKind);
    if (assignment != null) {
      if (
        !holdsVisibleNumber(mutation.container) ||
        mutation.container.serialNumber !== assignment.serialNumber ||
        mutation.container.numberAssignedAt !== assignment.assignedAt ||
        mutation.container.numberReleasedAt != null
      ) {
        throw new DomainError('La restauration du numéro visible est incohérente.', 'INVALID_STATE');
      }
    } else if (
      mutation.container.trackingMode !== 'internal-only' ||
      mutation.container.serialNumber != null ||
      mutation.container.numberAssignedAt != null ||
      mutation.container.numberReleasedAt != null
    ) {
      throw new DomainError('La restauration de l’aliment sans numéro est incohérente.', 'INVALID_STATE');
    }
    await transaction.objectStore('containers').put(mutation.container);
    await transaction.objectStore('events').add(mutation.event);
    await transaction.objectStore('meta').put({
      ...meta,
      deviceSequence: sequence,
      nextSerial:
        assignment == null
          ? meta.nextSerial
          : Math.max(meta.nextSerial, assignment.serialNumber + 1),
      updatedAt: context.now,
    });
    await transaction.done;
    return mutation;
  }

  async addFreezer(name: string, equipmentKind: Freezer['equipmentKind']): Promise<Freezer> {
    const database = await this.database();
    const meta = await database.get('meta', 'app');
    if (meta?.householdId == null) {
      throw new DomainError('Foyer local introuvable.', 'NOT_FOUND');
    }
    const freezer: Freezer = {
      id: randomId(),
      householdId: meta.householdId,
      name: name.trim(),
      equipmentKind,
      declaredTemperatureC: storagePolicyFor(equipmentKind).defaultDeclaredTemperatureC,
      archivedAt: null,
      createdAt: nowIso(),
    };
    if (!freezer.name) {
      throw new DomainError('Le nom de l’appareil est obligatoire.', 'INVALID_STATE');
    }
    await database.add('freezers', freezer);
    return freezer;
  }

  async addLocation(freezerId: string, name: string, parentId: string | null): Promise<StorageNode> {
    const database = await this.database();
    const freezer = await database.get('freezers', freezerId);
    if (freezer == null) {
      throw new DomainError('Appareil introuvable.', 'NOT_FOUND');
    }
    const siblings = (await database.getAllFromIndex('locations', 'by-freezer', freezerId)).filter(
      (location) => location.parentId === parentId,
    );
    if (parentId != null) {
      const allLocations = await database.getAllFromIndex('locations', 'by-freezer', freezerId);
      const byId = new Map(allLocations.map((location) => [location.id, location]));
      if (!byId.has(parentId)) {
        throw new DomainError(
          'Un emplacement et son parent doivent appartenir au même appareil.',
          'INVALID_STATE',
        );
      }
      let depth = 1;
      let cursor = byId.get(parentId);
      while (cursor?.parentId != null) {
        depth += 1;
        cursor = byId.get(cursor.parentId);
      }
      if (depth >= 3) {
        throw new DomainError('Trois niveaux maximum sous l’appareil.', 'LIMIT_REACHED');
      }
    }
    const location: StorageNode = {
      id: randomId(),
      freezerId,
      parentId,
      name: name.trim(),
      position: siblings.length,
      archivedAt: null,
    };
    if (!location.name) {
      throw new DomainError('Le nom de l’emplacement est obligatoire.', 'INVALID_STATE');
    }
    await database.add('locations', location);
    return location;
  }

  async saveAudit(record: AuditRecord): Promise<void> {
    const database = await this.database();
    const appliance = await database.get('freezers', record.freezerId);
    if (appliance != null && storagePolicyFor(appliance.equipmentKind).trackingMode !== 'visible-number') {
      throw new DomainError(
        'Un audit par numéro ne peut pas cibler un stockage sans numéro.',
        'INVALID_STATE',
      );
    }
    await database.put('audits', record);
  }

  async savePreferences(preferences: HouseholdPreferences): Promise<void> {
    assertRecipeShoppingLists(preferences.householdId, preferences.recipeShoppingLists ?? []);
    const database = await this.database();
    await database.put('preferences', normalizeHouseholdPreferences(preferences));
  }

  async mutatePreferences(
    householdId: string,
    factory: (current: HouseholdPreferences) => HouseholdPreferences,
  ): Promise<HouseholdPreferences> {
    const database = await this.database();
    const transaction = database.transaction('preferences', 'readwrite');
    const stored = await transaction.objectStore('preferences').get(householdId);
    if (stored == null) {
      await transaction.done;
      throw new Error('Préférences locales introuvables.');
    }
    const next = normalizeHouseholdPreferences(factory(normalizeHouseholdPreferences(stored)));
    if (next.householdId !== householdId) {
      await transaction.done;
      throw new Error('Une mutation de préférences ne peut pas changer de foyer.');
    }
    assertRecipeShoppingLists(householdId, next.recipeShoppingLists ?? []);
    await transaction.objectStore('preferences').put(next);
    await transaction.done;
    return structuredClone(next);
  }

  async savePersistentStorageStatus(
    status: InventorySnapshot['meta']['persistentStorage'],
  ): Promise<void> {
    const database = await this.database();
    const transaction = database.transaction('meta', 'readwrite');
    const meta = await transaction.objectStore('meta').get('app');
    if (meta == null) {
      await transaction.done;
      throw new Error('Métadonnées locales introuvables.');
    }
    await transaction.objectStore('meta').put({
      ...meta,
      persistentStorage: status,
      updatedAt: nowIso(),
    });
    await transaction.done;
  }

  async saveDemoSeededAt(value: string | null): Promise<void> {
    const database = await this.database();
    const transaction = database.transaction('meta', 'readwrite');
    const meta = await transaction.objectStore('meta').get('app');
    if (meta == null) {
      await transaction.done;
      throw new Error('Métadonnées locales introuvables.');
    }
    await transaction.objectStore('meta').put({
      ...meta,
      demoSeededAt: value,
      updatedAt: nowIso(),
    });
    await transaction.done;
  }

  async findEvent(eventId: string): Promise<InventoryEvent | null> {
    const database = await this.database();
    return (await database.get('events', eventId)) ?? null;
  }

  async replaceSnapshot(snapshot: InventorySnapshot, expected?: InventorySnapshot): Promise<void> {
    const candidate = validateInventorySnapshotCandidate(snapshot);
    const database = await this.database();
    const transaction = database.transaction(snapshotStores, 'readwrite');
    const transactionCompletion = transaction.done;
    void transactionCompletion.catch(() => undefined);
    if (expected != null && JSON.stringify(await this.readSnapshot(transaction)) !== JSON.stringify(expected)) {
      transaction.abort();
      await transactionCompletion.catch(() => undefined);
      throw new DomainError('L’inventaire a changé depuis le choix de la sauvegarde. Choisissez à nouveau le fichier.', 'RESTORE_CONFLICT');
    }
    const storesToReplace = [
        'households',
        'freezers',
        'locations',
        'containers',
        'events',
        'audits',
        'preferences',
        'temperatureReadings',
        'temperatureIncidents',
        'incidentResolutions',
      ] as const;
    await Promise.all(
      storesToReplace.map(async (storeName) => {
        await transaction.objectStore(storeName).clear();
      }),
    );

    const now = nowIso();
    const maxSerial = candidate.containers.reduce(
      (highest, container) => Math.max(highest, container.serialNumber ?? 0),
      0,
    );
    const restoredMeta: AppMeta = {
      ...candidate.meta,
      key: 'app',
      schemaVersion: SCHEMA_VERSION,
      deviceId: randomId(),
      deviceSequence: 0,
      nextSerial: Math.max(candidate.meta.nextSerial, maxSerial + 1),
      updatedAt: now,
    };
    await transaction.objectStore('meta').put(restoredMeta);
    if (candidate.household != null) {
      await transaction.objectStore('households').add(candidate.household);
    }
    for (const freezer of candidate.freezers) {
      await transaction.objectStore('freezers').add(freezer);
    }
    for (const location of candidate.locations) {
      await transaction.objectStore('locations').add(location);
    }
    for (const container of candidate.containers) {
      await transaction.objectStore('containers').add(container);
    }
    for (const event of candidate.events) {
      await transaction.objectStore('events').add(event);
    }
    for (const audit of candidate.audits) {
      await transaction.objectStore('audits').add(audit);
    }
    for (const reading of candidate.temperatureReadings) {
      await transaction.objectStore('temperatureReadings').add(reading);
    }
    for (const incident of candidate.temperatureIncidents) {
      await transaction.objectStore('temperatureIncidents').add(incident);
    }
    for (const resolution of candidate.incidentResolutions) {
      await transaction.objectStore('incidentResolutions').add(resolution);
    }
    if (candidate.preferences != null) {
      await transaction.objectStore('preferences').add(candidate.preferences);
    }
    await transactionCompletion;
  }

  async reconcileRotationAlerts(today: IsoDate): Promise<void> {
    const database = await this.database();
    const transaction = database.transaction('containers', 'readwrite');
    const containers = await transaction.objectStore('containers').getAll();
    const createdAt = nowIso();
    for (const container of containers) {
      const due = dueRotationAlerts(container, today, createdAt);
      if (due.length > 0) {
        await transaction.objectStore('containers').put({
          ...container,
          rotationAlerts: [...container.rotationAlerts, ...due],
        });
      }
    }
    await transaction.done;
  }

  async setRotationAlertState(
    containerId: string,
    threshold: FoodContainer['rotationAlerts'][number]['threshold'],
    state: FoodContainer['rotationAlerts'][number]['state'],
  ): Promise<void> {
    const database = await this.database();
    const transaction = database.transaction('containers', 'readwrite');
    const container = await transaction.objectStore('containers').get(containerId);
    if (container == null) {
      throw new DomainError('Contenant introuvable.', 'NOT_FOUND');
    }
    await transaction.objectStore('containers').put({
      ...container,
      rotationAlerts: container.rotationAlerts.map((alert) =>
        alert.threshold === threshold ? { ...alert, state } : alert,
      ),
    });
    await transaction.done;
  }

  async recordTemperatureReading(
    input: RecordTemperatureReadingInput,
    idempotencyKey: string,
  ): Promise<TemperatureReading> {
    const database = await this.database();
    const transaction = database.transaction(
      ['meta', 'freezers', 'temperatureReadings'],
      'readwrite',
    );
    const existing = await transaction
      .objectStore('temperatureReadings')
      .index('by-idempotency')
      .get(idempotencyKey);
    if (existing != null) {
      await transaction.done;
      return existing;
    }
    const [meta, freezer] = await Promise.all([
      transaction.objectStore('meta').get('app'),
      transaction.objectStore('freezers').get(input.freezerId),
    ]);
    if (meta?.householdId == null || freezer?.householdId !== meta.householdId) {
      throw new DomainError('Appareil froid local introuvable.', 'NOT_FOUND');
    }
    if (storagePolicyFor(freezer.equipmentKind).family === 'pantry') {
      throw new DomainError('La réserve ne prend pas en charge les relevés de température.', 'INVALID_STATE');
    }
    const count = await transaction.objectStore('temperatureReadings').count();
    if (count >= MAX_TEMPERATURE_READINGS) {
      throw new DomainError(
        `La limite locale de ${MAX_TEMPERATURE_READINGS} relevés est atteinte. Exportez une sauvegarde avant d’archiver des données.`,
        'LIMIT_REACHED',
      );
    }
    const reading = createManualTemperatureReading(
      randomId(),
      meta.householdId,
      input,
      nowIso(),
      idempotencyKey,
    );
    await transaction.objectStore('temperatureReadings').add(reading);
    await transaction.done;
    return reading;
  }

  async declareTemperatureIncident(
    input: DeclareTemperatureIncidentInput,
    idempotencyKey: string,
  ): Promise<TemperatureIncident> {
    const database = await this.database();
    const transaction = database.transaction(
      [
        'meta',
        'freezers',
        'locations',
        'containers',
        'events',
        'temperatureIncidents',
      ],
      'readwrite',
    );
    const existing = await transaction
      .objectStore('temperatureIncidents')
      .index('by-idempotency')
      .get(idempotencyKey);
    if (existing != null) {
      await transaction.done;
      return existing;
    }
    const [meta, freezer] = await Promise.all([
      transaction.objectStore('meta').get('app'),
      transaction.objectStore('freezers').get(input.freezerId),
    ]);
    if (meta?.householdId == null || freezer?.householdId !== meta.householdId) {
      throw new DomainError('Appareil froid local introuvable.', 'NOT_FOUND');
    }
    if (storagePolicyFor(freezer.equipmentKind).family === 'pantry') {
      throw new DomainError('La réserve ne prend pas en charge les incidents de température.', 'INVALID_STATE');
    }
    const count = await transaction.objectStore('temperatureIncidents').count();
    if (count >= MAX_TEMPERATURE_INCIDENTS) {
      throw new DomainError(
        `La limite locale de ${MAX_TEMPERATURE_INCIDENTS} incidents est atteinte.`,
        'LIMIT_REACHED',
      );
    }
    const [containers, events, locations] = await Promise.all([
      transaction
        .objectStore('containers')
        .index('by-household')
        .getAll(meta.householdId, MAX_CONTAINERS),
      transaction.objectStore('events').getAll(undefined, MAX_EVENTS + 1),
      transaction.objectStore('locations').getAll(),
    ]);
    assertEventHistoryWithinLimit(events.length);
    const createdAt = nowIso();
    const affectedContainerIds = findExposedContainerIds(
      input.freezerId,
      input.started,
      input.ended,
      createdAt,
      containers,
      events.filter((event) => event.householdId === meta.householdId),
      locations,
    );
    const incident = createTemperatureIncident(
      randomId(),
      meta.householdId,
      input,
      affectedContainerIds,
      createdAt,
      idempotencyKey,
    );
    await transaction.objectStore('temperatureIncidents').add(incident);
    await transaction.done;
    return incident;
  }

  async resolveIncidentExposure(
    input: ResolveIncidentExposureInput,
    idempotencyKey: string,
  ): Promise<{ incident: TemperatureIncident; resolution: IncidentResolution }> {
    const database = await this.database();
    const transaction = database.transaction(
      ['meta', 'temperatureIncidents', 'incidentResolutions', 'containers'],
      'readwrite',
    );
    const replay = await transaction
      .objectStore('incidentResolutions')
      .index('by-idempotency')
      .get(idempotencyKey);
    if (replay != null) {
      const replayedIncident = await transaction
        .objectStore('temperatureIncidents')
        .get(replay.incidentId);
      if (replayedIncident == null) {
        throw new DomainError('Incident local introuvable.', 'NOT_FOUND');
      }
      await transaction.done;
      return { incident: replayedIncident, resolution: replay };
    }
    const [meta, incident, container] = await Promise.all([
      transaction.objectStore('meta').get('app'),
      transaction.objectStore('temperatureIncidents').get(input.incidentId),
      transaction.objectStore('containers').get(input.containerId),
    ]);
    if (
      meta?.householdId == null ||
      incident?.householdId !== meta.householdId ||
      container?.householdId !== meta.householdId
    ) {
      throw new DomainError('Incident ou contenant local introuvable.', 'NOT_FOUND');
    }
    if (!incident.affectedContainerIds.includes(input.containerId)) {
      throw new DomainError(
        'Ce contenant ne fait pas partie de l’exposition enregistrée.',
        'INVALID_STATE',
      );
    }
    const authorLocal = input.authorLocal.trim();
    if (!authorLocal) {
      throw new DomainError('L’auteur local de la décision est obligatoire.', 'INVALID_STATE');
    }
    const count = await transaction.objectStore('incidentResolutions').count();
    if (count >= MAX_INCIDENT_RESOLUTIONS) {
      throw new DomainError(
        `La limite locale de ${MAX_INCIDENT_RESOLUTIONS} décisions est atteinte.`,
        'LIMIT_REACHED',
      );
    }
    const decidedAt = nowIso();
    const resolution: IncidentResolution = {
      id: randomId(),
      idempotencyKey,
      householdId: meta.householdId,
      incidentId: incident.id,
      containerId: container.id,
      authorLocal,
      decidedAt,
      choice: input.choice,
      comment: input.comment.trim(),
    };
    const previous = await transaction
      .objectStore('incidentResolutions')
      .index('by-incident')
      .getAll(incident.id);
    const updatedIncident: TemperatureIncident = {
      ...incident,
      status: deriveIncidentStatus(incident, [...previous, resolution]),
      updatedAt: decidedAt,
    };
    await transaction.objectStore('incidentResolutions').add(resolution);
    await transaction.objectStore('temperatureIncidents').put(updatedIncident);
    await transaction.done;
    return { incident: updatedIncident, resolution };
  }

  async deleteHousehold(
    householdId: string,
    signal?: AbortSignal,
  ): Promise<HouseholdDeletionResult> {
    if (!householdId.trim()) {
      throw new DomainError(
        'Identifiant du foyer manquant. Aucune donnée supprimée.',
        'INVALID_STATE',
      );
    }
    const database = await this.database();
    const transaction = database.transaction(
      [
        'meta',
        'households',
        'freezers',
        'locations',
        'containers',
        'events',
        'audits',
        'preferences',
        'temperatureReadings',
        'temperatureIncidents',
        'incidentResolutions',
        'householdDeletions',
      ],
      'readwrite',
    );
    const transactionDone = transaction.done;
    void transactionDone.catch(() => undefined);
    const abort = () => transaction.abort();
    signal?.addEventListener('abort', abort, { once: true });
    try {
      if (signal?.aborted) {
        transaction.abort();
      }
      const household = await transaction.objectStore('households').get(householdId);
      if (household == null) {
        await transactionDone;
        return { householdId, deleted: false };
      }

      const freezerStore = transaction.objectStore('freezers');
      const freezers = await freezerStore.index('by-household').getAll(householdId);
      const freezerIds = new Set(freezers.map((freezer) => freezer.id));
      const locationStore = transaction.objectStore('locations');
      const locations = (await locationStore.getAll()).filter((location) =>
        freezerIds.has(location.freezerId),
      );

      const deleteByHousehold = async (
        storeName:
          | 'containers'
          | 'events'
          | 'audits'
          | 'temperatureReadings'
          | 'temperatureIncidents'
          | 'incidentResolutions',
      ): Promise<void> => {
        const store = transaction.objectStore(storeName);
        const records = await store.getAll();
        for (const record of records) {
          if (record.householdId === householdId) {
            await store.delete(record.id);
          }
        }
      };

      await deleteByHousehold('incidentResolutions');
      await deleteByHousehold('temperatureIncidents');
      await deleteByHousehold('temperatureReadings');
      await deleteByHousehold('audits');
      await deleteByHousehold('events');
      await deleteByHousehold('containers');
      for (const location of locations) {
        await locationStore.delete(location.id);
      }
      for (const freezer of freezers) {
        await freezerStore.delete(freezer.id);
      }
      await transaction.objectStore('preferences').delete(householdId);
      await transaction.objectStore('households').delete(householdId);

      const metaStore = transaction.objectStore('meta');
      const meta = await metaStore.get('app');
      if (meta?.householdId === householdId) {
        await metaStore.put({
          ...meta,
          householdId: null,
          nextSerial: 1,
          demoSeededAt: null,
          updatedAt: nowIso(),
        });
      }
      await transaction.objectStore('householdDeletions').put({
        householdId,
        deletedAt: nowIso(),
      });
      await transactionDone;
      return { householdId, deleted: true };
    } finally {
      signal?.removeEventListener('abort', abort);
    }
  }

  async pendingHouseholdArtifactDeletions(): Promise<string[]> {
    const database = await this.database();
    return (await database.getAll('householdDeletions')).map(
      (receipt) => receipt.householdId,
    );
  }

  async completeHouseholdArtifactDeletion(householdId: string): Promise<void> {
    const database = await this.database();
    await database.delete('householdDeletions', householdId);
  }

  close(): void {
    if (this.databasePromise != null) {
      void this.databasePromise.then((database) => database.close());
      this.databasePromise = null;
    }
  }
}
